import cn from 'classnames'
import {
  getSubscriptionInfoById,
  getPaymentState,
  getAreaInfoById,
  changeUserEmail,
  getPaymentInfoById,
  startSubscriptionPaymentAsync,
  getUserInformation,
  setUserInfo,
  resetSubscriptionCost,
} from '@/store/slices/stagesSlice/stagesSlice';
import { Container, Button, Card, Title, Text, Time, CheckBox } from '@/components'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '@/store'
import unicode from '@/helpers/unicodeChars'
import formatNumberToString from '@/helpers/formatNumberToString'
import { useAreaStyles } from '@/helpers/hooks/useAreaStyles'
import { useRouter } from 'next/router'
import { KeyboardEvent, useEffect, useRef, useState } from 'react'
import CarNumberLabel from '../PaymentPage/CarNumberLabel'
import getSubscriptionInterval from '@/helpers/getSubscriptionInterval'
import styles from './SubscriptionPaymentPage.module.scss'
import EmailInput from '@/components/ui/EmailInput'
import { supportPhoneLink } from '@/constants'
import getLinkHref from '@/helpers/getLinkHref'
import { UserType } from '@/store/slices/stagesSlice/interfaces'
import { getConsentCheckboxTitle } from '@/helpers/getConsentCheckboxTitle'
import { getCookie } from 'cookies-next'
import { USER_DATA } from '@/components/pages/UserPage/config'

export default function SubscriptionPaymentPage({
  subscriptionId,
  number,
  stageId,
  areaId,
}: {
  subscriptionId: string
  number: string
  stageId: string | null
  areaId: string
}) {
  const fieldRef = useRef(null)
  const reduxState = useSelector<RootState, RootState>(state => state)
  const dispatch = useDispatch()
  const router = useRouter()
  useAreaStyles(router.query.areaId as string)
  const { data } = getSubscriptionInfoById(reduxState, subscriptionId)
  const subscriptionAdditionalFields = data?.subscription.additional_fields
  const { data: areaInfo } = getAreaInfoById(reduxState, areaId)
  const { data: sessionPaymentInfo } = getPaymentInfoById(reduxState, stageId)
  const user: UserType | null = getUserInformation(reduxState)
  const subscriptionCost = reduxState.stages.subscriptionCost.data
  const subscriptionCostError = reduxState.stages.subscriptionCost.error
    ? String(reduxState.stages.subscriptionCost.error)
    : undefined
  const userDataFromCookie = getCookie(USER_DATA)

  const checkAdditionalFields = () => {
    let isAdditionalFields = false
    for (const props in user) {
      if (props !== 'permissions' && user[props as keyof Omit<UserType, 'permissions'>]) {
        isAdditionalFields = true
      }
    }
    return isAdditionalFields
  }
  const isAdditionalFields = checkAdditionalFields()

  const { loading } = getPaymentState(reduxState)
  const [error, setError] = useState('')

  const [isConsentPersonalData, setIsConsentPersonalData] = useState(true)
  const [personalDataError, setPersonalDataError] = useState('')

  const [isConsentParkingRules, setIsConsentParkingRules] = useState(true)
  const [parkingRulesError, setParkingRulesError] = useState('')

  const [emailValue, setEmailValue] = useState('')
  const [emailInputError, setEmailInputError] = useState('')

  const price = `${formatNumberToString(subscriptionCost?.total_amount ?? 0)}${
    unicode.no_break_space
  }${unicode.ruble}`

  const subscriptionInterval = data
    ? getSubscriptionInterval({
        type_work: data.subscription.type_work,
        interval_type: data.subscription.interval_type,
        interval: data.subscription.interval,
        fixed_period_from: data.subscription.fixed_period_from,
        fixed_period_to: data.subscription.fixed_period_to,
      })
    : ''

  const getConsentDataProcessing = () => {
    if (isAdditionalFields) return isConsentPersonalData
    if (!isAdditionalFields && !emailValue) return false
    if (!isAdditionalFields && emailValue) return true
  }

  const handleStartPayment = async () => {
    const onSuccess = (orderNumber: string, subscriptionId: string) => {
      const paymentPendingPageHref = getLinkHref({
        type: 'payment-pending',
        data: { orderNumber, subscriptionId },
      })

      router.push(paymentPendingPageHref)
    }

    if (emailValue && !emailValue.match(/.+[@].+/gm)) {
      setEmailInputError('Укажите корректный email')
      return
    }

    const email = emailValue.trim()
    await dispatch(changeUserEmail({ email }))

    if (isAdditionalFields && !isConsentPersonalData) {
      setPersonalDataError('Подтвердите согласие на обработку персональных данных')
      return
    }

    if (!isConsentParkingRules) {
      setParkingRulesError('Подтвердите ознакомление с правилами пользования парковкой')
      return
    }

    if (subscriptionCostError) return

    await dispatch(
      startSubscriptionPaymentAsync({
        subscriptionId,
        number,
        permissions: {
          data_processing: getConsentDataProcessing() ?? false,
          parking_rules: isConsentParkingRules,
        },
        onSuccess,
        onError: (message: string) => setError(message),
      }),
    )
  }

  useEffect(() => {
    if (emailInputError) {
      const field = (fieldRef.current as unknown) as HTMLInputElement
      field?.focus()
    }
  }, [emailInputError])

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleStartPayment()
    }
  }

  const onChangeParkingRules = () => {
    setParkingRulesError('')
    setIsConsentParkingRules(prev => !prev)
  }

  const onChangePersonalData = () => {
    setPersonalDataError('')
    setIsConsentPersonalData(prev => !prev)
  }

  useEffect(() => {
    if (userDataFromCookie && typeof userDataFromCookie === 'string') {
      const user = { ...JSON.parse(userDataFromCookie) }
      if (subscriptionAdditionalFields) {
        const filteredUserInfo = Object.fromEntries(
          subscriptionAdditionalFields
            .map(field => field.name)
            .filter(name => name in user)
            .map(name => [name, user[name]]),
        )
        dispatch(setUserInfo(filteredUserInfo))
      }
    }
  }, [userDataFromCookie, subscriptionAdditionalFields])

  useEffect(() => {
    return () => {
      dispatch(resetSubscriptionCost())
    }
  }, [])

  if (!data)
    return (
      <Container>
        <Card color="decor">
          <div className={styles.notFound}>
            <div className={styles.notFound_Image}>
              <img src="/images/subscription-not-found.svg" alt="" />
            </div>
            <span>Нет информации по данному абонементу</span>
          </div>
        </Card>
      </Container>
    )

  const isShowSessionDetails = !!subscriptionCost?.stage?.amount
  const isShowFinesDetails = !!subscriptionCost?.fines_amount
  const sessionTime = sessionPaymentInfo?.range ?? 0

  return (
    <section className={styles.section}>
      <div className={styles.inner}>
        <Container>
          <header className={styles.header}>
            {subscriptionInterval && (
              <div>
                <Text className={styles.interval} isBold>
                  Длительность абонемента: {subscriptionInterval}
                </Text>
                <CarNumberLabel number={number} />
              </div>
            )}

            {sessionTime > 0 && (
              <div>
                <Text className={styles.interval} isBold>
                  Длительность сессии: <Time timeInSeconds={sessionTime} />
                </Text>
                <CarNumberLabel number={sessionPaymentInfo?.number ?? ''} />
              </div>
            )}
          </header>
          <div className={styles.paymentDetails}>
            <Title level={4} className={styles.paymentDetails_title}>
              К оплате
            </Title>
            <div className={styles.paymentDetails_row}>
              <Text level={2}>Абонемент</Text>
              <Text level={2} className={styles.paymentDetails_price}>
                {formatNumberToString(subscriptionCost?.subscription.amount ?? 0)}
                {unicode.no_break_space}
                {unicode.ruble}
              </Text>
            </div>
            {isShowSessionDetails && (
              <div className={styles.paymentDetails_row}>
                <Text level={2}>Парковочные услуги</Text>
                <Text level={2} className={styles.paymentDetails_price}>
                  {formatNumberToString(subscriptionCost.stage?.amount ?? 0)}
                  {unicode.no_break_space}
                  {unicode.ruble}
                </Text>
              </div>
            )}
            {isShowFinesDetails && (
              <div className={styles.paymentDetails_row}>
                <Text level={2}>Штраф на номер ТС</Text>
                <Text level={2} className={styles.paymentDetails_price}>
                  {formatNumberToString(subscriptionCost.fines_amount)}
                  {unicode.no_break_space}
                  {unicode.ruble}
                </Text>
              </div>
            )}
          </div>
          <div className={styles.totalAmount}>
            <p>Итого: </p>
            <p>{price}</p>
          </div>
        </Container>

        <Container className={styles.email_container}>
          <Title className={styles.content__title} level={4} size={24}>
            Укажите ваш email
          </Title>

          <Text level={2} className={styles.content__paragraph}>
            Ваш email необходим, чтобы мы могли прислать чек об успешной оплате.
          </Text>

          <label className={cn(styles.input, { [styles['input--active']]: !!emailValue })}>
            <EmailInput
              onChange={setEmailValue}
              onKeyPress={handleKeyPress}
              value={emailValue}
              error={emailInputError || error || subscriptionCostError}
            />
          </label>

          <div className={styles.checkBoxWrapper}>
            {isAdditionalFields && (
              <CheckBox
                name="dataProcessing"
                checked={isConsentPersonalData}
                title={
                  getConsentCheckboxTitle({
                    type: 'personalData',
                    linkProcessingPersonalData: areaInfo?.link_processing_personal_data,
                  }) ?? ''
                }
                onChange={onChangePersonalData}
              />
            )}
            {personalDataError && <Text className={styles.error}>{personalDataError}</Text>}

            <CheckBox
              name="rulesUsingParking"
              checked={isConsentParkingRules}
              title={
                getConsentCheckboxTitle({
                  type: 'parkingRules',
                  linkParkingRules: areaInfo?.link_parking_rules,
                }) ?? ''
              }
              onChange={onChangeParkingRules}
            />
            {parkingRulesError && <Text className={styles.error}>{parkingRulesError}</Text>}
          </div>
        </Container>

      </div>

      <Container className={styles.buttons}>
        <Button
          withLoader
          onClick={handleStartPayment}
          className={styles.button}
          loading={loading}
          background={areaInfo?.customising.button_back_color}
          disabled={!!subscriptionCostError}
        >
          Оплатить
        </Button>
        <Button component="a" href={supportPhoneLink} className={styles.button} isTransparent>
          Обратиться в поддержку
        </Button>
      </Container>
    </section>
  )
}
